import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useForm } from 'react-hook-form';
import { useTranslation } from '$hooks';
import { Flex, Grid } from '$components/layouts';
import { getCityFromZipcode } from '$services/zipcode.service';
import { IGiftShopDeliverySendModel, IGiftShopStoreList, ISpecialGiftShopDeliveryDatesAvailable } from '$models';
import { OverlayBottomContainer } from '../shared';
import { noLetterRegex, noNumberRegex } from '$constants';
import { useAppStore } from '~/store';
import { useGiftShop } from '~/hooks/use-gift-shop';
import { Text } from '$components/elements/text';
import { InputField } from '$components/elements/input-field';
import { DateSelector } from '$components/elements/date-selector';
import { Button } from '$components/elements/button';
import { formatStringWithContent } from '$lib/helpers';

type Props = {
    itemNumber?: string;
    editStep?: boolean;
    deliveryInfo?: IGiftShopDeliverySendModel;
    onSubmitDelivery?: (values: IGiftShopDeliverySendModel) => void;
    onCloseModal?: () => void;
};

type FormData = {
    name: string;
    streetAndNumber: string;
    companyName?: string;
    zip: string;
    city: string;
    phoneNumber: number;
    occasion: string;
};

export const GiftShopDeliverySend: FC<Props> = ({
    deliveryInfo,
    editStep,
    onSubmitDelivery,
    onCloseModal,
}): JSX.Element => {
    const { translate } = useTranslation();
    const { getSpecialDeliveryDatesAvailable, getAvailableShops } = useGiftShop();
    const [foundCity, setFoundCity] = useState('');
    const [availableStoreData, setAvailableStoreData] = useState<IGiftShopStoreList>();
    const [deliveryDataAvailability, setDeliveryDataAvailability] =
        useState<ISpecialGiftShopDeliveryDatesAvailable | null>();
    const [selectedDate, setSelectedDate] = useState<Date>();
    const theme = useTheme();
    const isApp = useAppStore((state) => state.isApp);

    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        trigger,
        formState: { errors, dirtyFields },
    } = useForm<FormData>({ reValidateMode: 'onChange' });

    useEffect(() => {
        if (deliveryInfo) {
            handlePrefilledData(deliveryInfo);
        }
    }, [deliveryInfo]);

    const handlePrefilledData = async (deliveryInfo: IGiftShopDeliverySendModel) => {
        if (deliveryInfo) {
            setValue('name', deliveryInfo.name || '', { shouldDirty: true });
            setValue('streetAndNumber', deliveryInfo.address || '', { shouldDirty: true });
            setValue('city', deliveryInfo.city || '', { shouldDirty: true });
            setValue('companyName', deliveryInfo.companyName || '', { shouldDirty: true });
            setValue('occasion', deliveryInfo.occasion || '', { shouldDirty: true });
            if (deliveryInfo.phoneNumber) {
                setValue('phoneNumber', +deliveryInfo.phoneNumber, { shouldDirty: true });
            }
        }

        if (deliveryInfo?.zipcode) {
            setValue('zip', deliveryInfo.zipcode, { shouldDirty: true });
            await getAvailabilityFromZip(deliveryInfo.zipcode);
            if (deliveryInfo.city) {
                setValue('city', deliveryInfo.city, { shouldDirty: true });
                setFoundCity(deliveryInfo.city);
            }
        }
        if (deliveryInfo?.deliveryDate) {
            setSelectedDate(new Date(deliveryInfo.deliveryDate));
            const shops = await getAvailableShops(getValues('zip'), new Date(deliveryInfo.deliveryDate));
            if (shops) {
                setAvailableStoreData(shops);
            }
        }
    };

    const [zipMatched, setZipMatched] = useState(true);

    const handleZipcodeCity = async (zipcode: string) => {
        const city = getCityFromZipcode(zipcode);
        if (city) {
            setValue('city', city, { shouldDirty: true });
            setFoundCity(city);
            trigger('city');
            setZipMatched(true);
        } else {
            resetCity();
            setZipMatched(false);
        }
        await getAvailabilityFromZip(zipcode);
        if (selectedDate) {
            onDatePickerChange(selectedDate);
        }
    };

    const getAvailabilityFromZip = async (zipcode: string) => {
        const availability = await getSpecialDeliveryDatesAvailable(zipcode);
        if (availability) {
            setDeliveryDataAvailability(availability);
        } else {
            setDeliveryDataAvailability(null);
        }
    };

    const onDatePickerChange = async (date?: Date) => {
        if (date) {
            setSelectedDate(date);

            const shops = await getAvailableShops(getValues('zip'), date);
            if (shops) {
                setAvailableStoreData(shops);
            }
        }
    };

    const resetCity = () => {
        setValue('city', '');
        setFoundCity('');
    };

    const onSubmit = handleSubmit((data) =>
        onSubmitDelivery?.({
            deliveryDate: selectedDate?.toISOString(),
            storeId: availableStoreData?.stores?.[0]?.id ?? '',
            phoneNumber: data.phoneNumber.toString(),
            address: data.streetAndNumber,
            city: data.city,
            companyName: data.companyName,
            name: data.name,
            zipcode: data.zip,
            occasion: data.occasion,
        })
    );

    return (
        <form onSubmit={onSubmit}>
            <Text h4 size={theme.fontSizes.md} fontWeight="Bold" font="heading">
                {translate('giftShop.delivery.sendDeliveryHeading')}
            </Text>
            <Flex column>
                <Grid flow="row" columnGap="extrasmall" columns="25% 1fr">
                    <InputField
                        spacingBottom="small"
                        defaultValue={deliveryInfo?.zipcode}
                        label={translate('checkout.contactForm.zipcode')}
                        isOuterLabel
                        error={errors.zip}
                        maxLength={4}
                        type="tel"
                        {...register('zip', {
                            required: translate('validation.error.zipcodeRequired'),
                            pattern: { value: /[0-9]{4}/, message: translate('validation.error.zipcodeInvalid') },
                        })}
                        onChangeSideEffect={(e) => e.target.value.length === 4 && handleZipcodeCity(e.target.value)}
                    />
                    <InputField
                        spacingBottom="small"
                        label={translate('checkout.contactForm.city')}
                        isOuterLabel
                        error={errors.city}
                        readOnly
                        defaultValue={foundCity || ''}
                        value={foundCity || ''}
                        {...register('city', {
                            required: translate('validation.error.noZipcodeCityFound'),
                        })}
                    />
                </Grid>
                {!zipMatched && dirtyFields.zip ? (
                    <CityNotFoundErrorMessage>
                        {formatStringWithContent(
                            translate('checkout.contactForm.deliveryToZipCode'),
                            <a onClick={() => onCloseModal?.()}>
                                {translate('checkout.contactForm.deliveryToZipCodeGoBack')}
                            </a>
                        )}
                    </CityNotFoundErrorMessage>
                ) : null}
            </Flex>
            <DateSelector
                label={translate('giftShop.form.deliveryDateLabel')}
                selected={selectedDate}
                onChange={(value) => onDatePickerChange(value)}
                disabled={!deliveryDataAvailability}
                placeholder={translate('giftShop.form.deliveryDatePlaceholder')}
                sameDayAvailability={
                    deliveryDataAvailability ? deliveryDataAvailability.sameDayDeliveryPossible : undefined
                }
                nextDayAvailability={
                    deliveryDataAvailability ? deliveryDataAvailability.dayToDayDeliveryPossible : undefined
                }
                spacingBottom="medium"
            />
            <Text>{translate('giftShop.form.storeLabel')}</Text>

            <Text h5 size={theme.fontSizes.md} fontWeight="Bold" font="heading">
                {translate('giftShop.delivery.sendRecipientHeading')}
            </Text>
            <InputField
                spacingBottom="extrasmall"
                defaultValue={deliveryInfo?.name}
                label={translate('checkout.contactForm.name')}
                isOuterLabel
                error={errors.name}
                {...register('name', {
                    required: translate('validation.error.nameRequired'),
                })}
            />
            <InputField
                defaultValue={deliveryInfo?.companyName}
                label={translate('checkout.contactForm.companyName')}
                isOuterLabel
                error={errors.companyName}
                {...register('companyName')}
                spacingBottom="extrasmall"
            />
            <InputField
                defaultValue={deliveryInfo?.address}
                label={translate('checkout.contactForm.address')}
                isOuterLabel
                error={errors.streetAndNumber}
                {...register('streetAndNumber', {
                    required: translate('validation.error.addressRequired'),
                    validate: {
                        NoNumber: (value) => noLetterRegex.test(value) || translate('validation.error.addressNoRoad'),
                        NoLetter: (value) => noNumberRegex.test(value) || translate('validation.error.addressNoNumber'),
                    },
                })}
                spacingBottom="extrasmall"
            />
            <InputField
                defaultValue={deliveryInfo?.phoneNumber}
                label={translate('giftShop.form.phoneNumberLabel')}
                isOuterLabel
                outerSubLabel={translate('giftShop.form.phoneNumberSubLabel')}
                error={errors.phoneNumber}
                type="tel"
                {...register('phoneNumber', {
                    required: translate('giftShop.form.phoneNumberValidationRequired'),
                    pattern: { value: /[0-9]{8}/, message: translate('giftShop.form.phoneNumberValidationInvalid') },
                })}
                spacingBottom="extrasmall"
            />

            <OverlayBottomContainer justifyContent="center" isApp={isApp}>
                <Button width="expansive" type="submit">
                    {editStep
                        ? translate('giftShop.delivery.updateInformation')
                        : translate('giftShop.delivery.addToBasket')}
                </Button>
            </OverlayBottomContainer>
        </form>
    );
};

const CityNotFoundErrorMessage = styled(Text)(({ theme }) => ({
    fontSize: theme.fontSizes.sm,
    marginBottom: theme.space[5],
}));
